import clsx from 'clsx'
import React, { useContext, useState, type PropsWithChildren } from 'react'

import config from 'tailwind.config'

import { Icon } from '@/components/Icon'
import { Modal } from '@/components/Modal/Modal'
import useMediaQuery from '@/hooks/useMediaQuery'
import type { CMSCollectionFilterGroup } from '@/types/sanity'

import { CollectionContext } from './collection-context'
import { FilterFlyout } from './FilterFlyout'
import { SortSelect } from './SortSelect'
import { FilterPanel } from './FilterPanel'

import './FilterAndSort.scss'

export const FilterAndSort: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    filters,
    appliedFilters,
    sortby,
    handleFilterChange,
    setAppliedFilters,
  } = useContext(CollectionContext)

  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false)

  const isDesktopScreen = useMediaQuery(
    `(min-width: ${config.theme.screens.md})`,
  )

  const removeAppliedFilter = (filterGroup: CMSCollectionFilterGroup) => {
    handleFilterChange({ filter: undefined, filterGroup })
  }

  const toggleFiltersMenu = () => {
    setIsFiltersMenuOpen(!isFiltersMenuOpen)
  }

  const closeFiltersMenu = () => {
    setIsFiltersMenuOpen(false)
  }

  const showAppliedFilters =
    filters?.length > 0 &&
    !isFiltersMenuOpen &&
    Object.keys(appliedFilters).length > 0

  return (
    <div className="collection-filter-and-sort">
      <div className="hidden md:block">{children}</div>
      <div className="collection-filter-and-sort__toggles">
        {filters?.length > 0 && (
          <div
            className={clsx(
              'collection-filter-toggle',
              isFiltersMenuOpen && 'collection-filter-toggle--open',
            )}
            onClick={toggleFiltersMenu}
          >
            <span className="type-label">Filter</span>
            {isDesktopScreen && (
              <span className="collection-filter-toggle__icon">
                <Icon name="chevron" />
              </span>
            )}
          </div>
        )}
        {sortby && (
          <div className="collection-sort-toggle">
            <SortSelect isDesktopScreen={isDesktopScreen} />
          </div>
        )}
      </div>
      {filters?.length > 0 &&
        (isDesktopScreen ? (
          <FilterPanel isFiltersMenuOpen={isFiltersMenuOpen} />
        ) : (
          <Modal
            isOpen={isFiltersMenuOpen}
            onClose={closeFiltersMenu}
            header={
              <div className="collection-filter-flyout__header type-h5">
                Filter
              </div>
            }
          >
            <FilterFlyout
              isFiltersMenuOpen={isFiltersMenuOpen}
              closeFiltersMenu={closeFiltersMenu}
              applyFilters={setAppliedFilters}
            />
          </Modal>
        ))}
      <div className="md:hidden">{children}</div>
      {showAppliedFilters && (
        <div className="collection-applied-filters">
          {filters
            .filter((filterGroup) => appliedFilters[filterGroup._id])
            .map((filterGroup) => (
              <div
                className="collection-applied-filters__item button button--tertiary"
                key={filterGroup._id}
              >
                {appliedFilters[filterGroup._id].label}
                <span
                  className="collection-applied-filters__item-icon"
                  onClick={() => removeAppliedFilter(filterGroup)}
                >
                  <Icon name="cross" />
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
